// ─────────────────────────────────────────────────────────────────────
// TELLING THE OTHER TABS THAT THE CREDENTIAL MOVED (#773).
//
// `clubSession` is one JavaScript instance per tab. A step-up rotates the
// bearer in the tab that ran the ceremony and the server revokes the previous
// one — so a second tab goes on holding a revoked token, sends it, gets 401,
// and signs itself out. Stepping up in one tab logged you out of the others.
//
// ── WHAT CROSSES THE CHANNEL IS A FACT, NEVER A CREDENTIAL ─────────
//
// The notice says THAT a rotation happened. It does not carry the new bearer,
// and it does not need to: the step-up response already re-issued the
// HttpOnly session cookie (apps/api/src/routes/stepUp.ts), and cookies are
// shared across tabs on an origin. Every other tab's durable credential is
// therefore already correct the moment the ceremony completes. The only thing
// wrong in those tabs is the stale in-memory bearer SUPPRESSING it — the API
// consults the cookie only when there is no Authorization header at all.
//
// So a receiving tab drops its bearer and the cookie speaks. Nothing is
// written to localStorage, sessionStorage or IndexedDB, and the message shape
// below has no field a token could travel in.
//
// ── IT IS ALLOWED TO BE ABSENT ─────────────────────────────────────
//
// `BroadcastChannel` is missing in older WebViews and in some test and SSR
// environments, and a suspended WebView does not receive a message it was not
// awake for. So this is a best-effort fast path, not a guarantee, and nothing
// about correctness may depend on delivery. A tab a notice never reaches finds
// out on its first refused request instead: `clubSession.checkRefusal` asks
// the cookie before anything is ended (#783), so what the notice saves is a
// refused round trip, never the session.
// ─────────────────────────────────────────────────────────────────────

/** The one notice there is. No token, by construction. */
export interface SessionNotice {
  kind: 'club-credential-rotated';
  /**
   * WHOSE credential moved. A tab signed in as somebody else must not drop its
   * bearer: the cookie is shared, so a tab holding a bearer for A while the
   * cookie belongs to B would otherwise fall back onto B's credential and act
   * as B under A's screen.
   */
  accountId: string;
  /**
   * WHICH INSTANCE SENT IT, so a channel never acts on its own notice. The
   * specification does not deliver a message to the object that posted it, but
   * two instances inside one tab would — and the tab that just rotated is
   * precisely the one whose fresh bearer must survive.
   */
  origin: string;
}

export interface SessionChannel {
  post(notice: SessionNotice): void;
  subscribe(listener: (notice: SessionNotice) => void): () => void;
  close(): void;
  /** This instance's id — the `origin` its own notices carry. */
  readonly id: string;
}

export const SESSION_CHANNEL_NAME = 'starpoint.session.v1';

/** A notice from an older or newer build is ignored rather than guessed at. */
function readNotice(data: unknown): SessionNotice | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (d.kind !== 'club-credential-rotated') return null;
  if (typeof d.accountId !== 'string' || d.accountId === '') return null;
  if (typeof d.origin !== 'string' || d.origin === '') return null;
  return { kind: 'club-credential-rotated', accountId: d.accountId, origin: d.origin };
}

/** A channel that does nothing, for an environment without BroadcastChannel. */
export function createInertSessionChannel(id = 'inert'): SessionChannel {
  return {
    id,
    post: () => undefined,
    subscribe: () => () => undefined,
    close: () => undefined,
  };
}

export function createSessionChannel(name: string = SESSION_CHANNEL_NAME): SessionChannel {
  const id = newId();
  if (typeof BroadcastChannel !== 'function') return createInertSessionChannel(id);

  let channel: BroadcastChannel;
  try {
    channel = new BroadcastChannel(name);
  } catch {
    // Some privacy modes throw on construction rather than omitting the API.
    return createInertSessionChannel(id);
  }

  const listeners = new Set<(n: SessionNotice) => void>();
  channel.onmessage = (event: MessageEvent) => {
    const notice = readNotice(event.data);
    if (!notice) return;
    if (notice.origin === id) return;
    for (const l of [...listeners]) l(notice);
  };

  return {
    id,
    post(notice) {
      try {
        channel.postMessage(notice);
      } catch {
        // A closed or broken channel must never break the rotation that is
        // reporting through it. The rotation itself has already succeeded.
      }
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    close() {
      listeners.clear();
      try {
        channel.close();
      } catch {
        // Already closed.
      }
    },
  };
}

function newId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `t${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}
